import { KEY_STORAGE } from 'constants/common'
import moment from 'moment'
import { Dispatch, createContext, useContext, useReducer } from 'react'
import { logout as doLogout, getSessionExpired, setRefreshTokenExpiresAt, setSecureItem, setTokenExpiresAt } from 'utils/app.utils'

export type LoginContextData = {
  isLoggedIn: boolean
  token?: string
}

export type LoginContextPayload = {
  loginData: LoginContextData
  loginDispatch: Dispatch<any>
}

export type LoginContextAction = {
  type: string
  data: LoginContextData
}

export const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
export const LOGIN_FAILED = 'LOGIN_FAILED'
export const LOGOUT = 'LOGOUT'

const LoginContext = createContext<LoginContextPayload>({} as LoginContextPayload)

export function loginFailed(): LoginContextAction {
  return { type: LOGIN_FAILED, data: { isLoggedIn: false } }
}

export function logout(): LoginContextAction {
  return { type: LOGOUT, data: { isLoggedIn: false } }
}

export function loginSuccess(
  token: string,
  remember: boolean,
  refreshToken?: string,
  tokenExpiresAt?: string,
  refreshTokenExpiresAt?: string
): LoginContextAction {
  setSecureItem(KEY_STORAGE.TOKEN, token)

  // Set token expiration (from backend or calculate 12 hours from now)
  const expiresAt = tokenExpiresAt || moment().add(12, 'hours').format('YYYY-MM-DD HH:mm:ss')
  setTokenExpiresAt(expiresAt)

  // Set refresh token if provided
  if (refreshToken) {
    setSecureItem(KEY_STORAGE.REFRESH_TOKEN, refreshToken)
    const refreshExpiresAt = refreshTokenExpiresAt || moment().add(7, 'days').format('YYYY-MM-DD HH:mm:ss')
    setRefreshTokenExpiresAt(refreshExpiresAt)
  }

  if (!remember) setSecureItem(KEY_STORAGE.SESSION_EXPIRED, String(moment().add(1, 'days').set({ 'hour': 4, 'minute': 59 }).format('YYYY-MM-DD HH:mm')))
  return { type: LOGIN_SUCCESS, data: { isLoggedIn: true, token } }
}

export function loginReducer(state: any, action: LoginContextAction) {
  if (action.type === LOGIN_SUCCESS) return action.data
  else return { isLoggedIn: false }
}

export const checkRememberLogin = async () => {
  const sessionDate = getSessionExpired()
  if (sessionDate) {
    const now = moment()
    const session = moment(sessionDate, 'YYYY-MM-DD HH:mm')
    const expired = now.isAfter(session)
    if (expired) {
      await doLogout()
      window.location.reload()
    }
  }
}

const initialLoginState: LoginContextData = { isLoggedIn: false }

function LoginProvider(props: any) {
  const [state, dispatch]: [LoginContextData, any] = useReducer(loginReducer, initialLoginState)
  const data = { loginData: state, loginDispatch: dispatch }
  return <LoginContext.Provider value={data} {...props} />
}

function useLoginContext(): LoginContextPayload {
  return useContext(LoginContext)
}

export { LoginProvider, useLoginContext }
